import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Camera, Upload, X, CheckCircle2, FileText, Image as ImageIcon, LogOut } from 'lucide-react';
import { publicAnonKey } from '../../../utils/supabase/info';

const API_BASE_URL = 'https://xdqdkwpfihrapeczceqr.supabase.co/functions/v1/make-server-28f1cccf';

interface Customer {
  id: string;
  name: string;
  orgNumber?: string;
}

interface FilePreview {
  file: File;
  preview?: string;
  type: 'image' | 'document';
}

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FilePreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Check if authenticated via magic link
    const customerId = localStorage.getItem('customer_id');
    const customerName = localStorage.getItem('customer_name');
    const magicToken = localStorage.getItem('magic_token');

    if (!customerId || !customerName || !magicToken) {
      toast.error('Ugyldig tilgang. Bruk lenken fra e-posten du mottok.');
      navigate('/');
      return;
    }

    setCustomer({
      id: customerId,
      name: customerName
    });
  }, [navigate]);

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const newFiles: FilePreview[] = [];
    const maxSize = 50 * 1024 * 1024; // 50MB

    Array.from(files).forEach((file) => {
      // Check file size
      if (file.size > maxSize) {
        toast.error(`${file.name} er for stor (maks 50MB)`);
        return;
      }

      // Check file type
      const fileType = file.type;
      const isImage = fileType.startsWith('image/');
      const isPDF = fileType === 'application/pdf';
      const isDoc = fileType === 'application/msword' || fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      if (!isImage && !isPDF && !isDoc) {
        toast.error(`${file.name} er ikke en støttet filtype`);
        return;
      }

      const preview: FilePreview = {
        file,
        type: isImage ? 'image' : 'document',
      };

      // Create preview for images
      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          preview.preview = reader.result as string;
          setSelectedFiles((prev) => [...prev, preview]);
        };
        reader.readAsDataURL(file);
      } else {
        newFiles.push(preview);
      }
    });

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleFileClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!customer || selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Create FormData for multipart upload
      const formData = new FormData();
      formData.append('customerId', customer.id);
      formData.append('customerName', customer.name);
      
      // Add all files
      selectedFiles.forEach((filePreview) => {
        formData.append('files', filePreview.file);
      });

      const magicToken = localStorage.getItem('magic_token');

      // Upload to backend
      const response = await fetch(`${API_BASE_URL}/customer-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'X-Magic-Token': magicToken || ''
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle token-related errors
        if (response.status === 401) {
          toast.error('Din tilgang har utløpt eller er ugyldig. Vennligst be om en ny lenke fra Nordic Drift.');
          setTimeout(() => {
            handleLogout();
          }, 2000);
          return;
        }
        
        throw new Error(errorData.error || 'Opplasting feilet');
      }

      const result = await response.json();
      console.log('Upload result:', result);

      setUploadProgress(100);
      setIsSuccess(true);
      toast.success(`${selectedFiles.length} fil(er) lastet opp!`);
    } catch (error) {
      console.error('Upload error:', error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('En feil oppstod under opplasting');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFiles([]);
    setIsSuccess(false);
    setUploadProgress(0);
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_id');
    localStorage.removeItem('customer_name');
    localStorage.removeItem('magic_token');
    toast.success('Logget ut');
    navigate('/');
  };

  if (!customer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Laster...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-blue-900 truncate">
              {customer?.name || 'Laster...'}
            </h1>
            <p className="text-xs text-gray-600">Nordic Drift Kundeportal</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="gap-2"
          >
            <LogOut className="w-4 h-4" />
            Logg ut
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {isSuccess ? (
          <Card className="text-center py-12">
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <div className="bg-green-100 p-4 rounded-full">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
              </div>
              <div>
                <h2 className="text-2xl font-semibold mb-2">Opplasting vellykket!</h2>
                <p className="text-gray-600">
                  {selectedFiles.length} fil(er) ble lastet opp for {customer?.name}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={handleReset} className="gap-2">
                  <Upload className="w-4 h-4" />
                  Last opp flere filer
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Upload Buttons */}
            {selectedFiles.length === 0 && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-2xl font-semibold mb-2">Last opp dokumenter</h2>
                  <p className="text-gray-600">
                    Ta bilder eller velg filer fra enheten din
                  </p>
                </div>

                <div className="grid gap-4">
                  <Card
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={handleCameraClick}
                  >
                    <CardContent className="p-8 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="bg-blue-100 p-6 rounded-full">
                          <Camera className="w-10 h-10 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-1">📸 Ta bilde</h3>
                          <p className="text-sm text-gray-600">
                            Åpne kamera for å ta bilde
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={handleFileClick}
                  >
                    <CardContent className="p-8 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="bg-green-100 p-6 rounded-full">
                          <Upload className="w-10 h-10 text-green-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold mb-1">📁 Last opp filer</h3>
                          <p className="text-sm text-gray-600">
                            Velg bilder eller dokumenter (maks 50MB)
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,.pdf,.doc,.docx"
                  multiple
                  onChange={(e) => handleFileSelect(e.target.files)}
                  className="hidden"
                />
              </div>
            )}

            {/* File Preview */}
            {selectedFiles.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    Valgte filer ({selectedFiles.length})
                  </h3>
                  {!isUploading && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Legg til flere
                    </Button>
                  )}
                </div>

                <div className="grid gap-3">
                  {selectedFiles.map((filePreview, index) => (
                    <Card key={index}>
                      <CardContent className="p-4 flex items-center gap-3">
                        {filePreview.type === 'image' && filePreview.preview ? (
                          <img
                            src={filePreview.preview}
                            alt={filePreview.file.name}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                            {filePreview.type === 'image' ? (
                              <ImageIcon className="w-8 h-8 text-gray-400" />
                            ) : (
                              <FileText className="w-8 h-8 text-gray-400" />
                            )}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">
                            {filePreview.file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(filePreview.file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        {!isUploading && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Upload Progress */}
                {isUploading && (
                  <Card>
                    <CardContent className="p-6">
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Laster opp...</span>
                          <span>{uploadProgress}%</span>
                        </div>
                        <Progress value={uploadProgress} />
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Upload Button */}
                {!isUploading && (
                  <Button
                    onClick={handleUpload}
                    className="w-full h-14 text-lg gap-2"
                    size="lg"
                  >
                    <Upload className="w-5 h-5" />
                    Last opp {selectedFiles.length} fil(er)
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}